import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FileUploadsOptions, FileUploadsResult, UploadedFile } from './types';
import { useFileUploadActions } from './useFileUploadActions';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';

export const useFileUploads = ({
  autoRefreshProcessing = false,
  clientId: overrideClientId,
  familyId: overrideFamilyId,
}: FileUploadsOptions = {}): FileUploadsResult => {
  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const { clientId: defaultClientId } = useCurrentClient();
  const actions = useFileUploadActions();
  const clientId = overrideClientId ?? defaultClientId;

  const queryKey = overrideFamilyId
    ? ['families', overrideFamilyId, 'data-upload-files']
    : ['clients', clientId, 'data-upload-files'];

  const {
    data,
    isLoading: isLoadingFiles,
    isError: isLoadError,
    refetch: refetchFiles,
  } = useQuery<UploadedFile[], Error>({
    queryKey,
    refetchInterval: refetchInterval || undefined,
  });

  const files =
    data?.map(file => ({
      id: file.id,
      file: {
        id: file.file.id,
        fileName: file.file.fileName,
        fileSize: file.file.fileSize,
        fileType: file.file.fileType,
        isFolder: file.isFolder,
        isResolved: file.isResolved,
        isFiled: file.file.isFiled || false,
        isTemporary: file.file.isTemporary || false,
        createdAt: file.createdAt,
        parentId: file.parentId,
        templateId: file.file.templateId,
      },
      clientId: file.clientId,
      isFolder: file.isFolder,
      isResolved: file.isResolved,
      createdAt: file.createdAt,
      parentId: file.parentId,
      templateId: file.file.templateId,
      backgroundProcess: file.backgroundProcess,
      fileName: file.file.fileName,
      fileSize: file.file.fileSize,
      fileType: file.file.fileType,
      dataUploadModel: file.dataUploadModel,
    })) || [];

  useEffect(() => {
    if (
      autoRefreshProcessing &&
      files.some(file => ['PROCESSING', 'PENDING'].includes(file.backgroundProcess?.status || ''))
    ) {
      setRefetchInterval(5000);
    } else {
      setRefetchInterval(false);
    }
  }, [files, autoRefreshProcessing]);

  const fileIsAdded = (file: UploadedFile): boolean => {
    return !file.isFolder && (!file.isResolved || !file.file.isFiled);
  };

  const filesToAdd = useMemo(() => {
    return files.filter(fileIsAdded);
  }, [files]);

  const allOtherFiles = useMemo(() => {
    return files.filter(file => !fileIsAdded(file));
  }, [files]);

  return {
    files,
    filesToAdd,
    allOtherFiles,
    ...actions,
    isLoading: isLoadingFiles,
    error: isLoadError ? new Error('Failed to load files') : actions.error,
    refetchFiles,
  };
};
